// library imports
import { useState, useEffect } from 'react';
import { useNavigate, useLocation  } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// User Component imports
import { Button, Icon, Stack } from "_core/components/common/UI";
import * as SceneConfig from "_core/configs/SceneConfig";
import { deviceType, useDevice } from '_core/managers/ResponsiveManager';
import { setMenuState, setInitializeState, setLoading, setErrorModal } from '_core/slices/scene/SceneSlice';
import * as Constants from "_core/configs/Constants";
import * as LocalStorageUtils from "_core/utils/localstorage";
import { canManageAccount, canUseOperatorTool, canCreateTenant } from "_core/utils/common";
import UsersLogoutApi from "apis/UsersLogoutApi";

// styles imports
import './Menu.scss';

function GetNavigatePath(destination){
  return Constants.REACT_APP_BASE_PATH + "/" + destination.join("/")
}

// メインのコンポーネント定義
export default function Menu(props) {
  const { scene, parts, isMenu, isMenuOpen } = useSelector((store) => store.scene);
  const { initializeState } = useSelector((store) => store.scene);
  const { tUser, tUserAdd, tAccount, tAppearanceList } = useSelector((store) => store.transaction);
  const isOpsHome = canCreateTenant(tAccount?.auth, tUser);
  const device = useDevice();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const CallUsersLogoutApi = UsersLogoutApi();
  const [isAppearanceEmpty, setIsAppearanceEmpty] = useState(true);
  const missingKey = (LocalStorageUtils.GetItem(Constants.LOCALSTORAGE_KEYS.serviceType) == "" || LocalStorageUtils.GetItem(Constants.LOCALSTORAGE_KEYS.serviceType) == null)
  const [logo, setLogo] = useState(missingKey ? (Constants.REACT_APP_BASE_PATH + '/img/PRizmo_rogo_big_mono.png') : (Constants.REACT_APP_BASE_PATH + `/img/${Constants.SERVICE_TYPE_LABELS[LocalStorageUtils.GetItem(Constants.LOCALSTORAGE_KEYS.serviceType)]}/PRizmo_rogo_big.png`));

  // メニューの開閉状態に応じてCSS変数を更新
  useEffect(() => {
    const menuWidth = isMenuOpen ? '240px' : '64px';
    document.documentElement.style.setProperty('--menu-current-width', menuWidth);
  }, [isMenuOpen]);

  // サービスタイプで画像切り替え
  useEffect(() => {
    if (!tUser){
      return;
    }

    setLogo(Constants.REACT_APP_BASE_PATH + `/img/${Constants.SERVICE_TYPE_LABELS[tUser.service_type]}/PRizmo_rogo_big.png`)
  }, [tUser]);

  // tAppearanceの変化を監視
  useEffect(() => {
    // tAppearanceが存在し、かつ空でないかをチェック
    const isEmpty = !tAppearanceList || Object.keys(tAppearanceList).length === 0;
    setIsAppearanceEmpty(isEmpty);
  }, [tAppearanceList]);

  const onChangeMenu = () => {
    dispatch(setMenuState({ isMenuOpen: !isMenuOpen }));
  };

  const onClickSceneButton = (destination) => {
    dispatch(setInitializeState(Constants.INITIALIZE_STATE.ready))
    navigate(GetNavigatePath(destination));
  };

  const onClickLogoutButton = async () => {
    dispatch(setLoading(true));

    const { response } = await CallUsersLogoutApi();

    if (response && response.error_code === 0) {
      LocalStorageUtils.SetItem(Constants.LOCALSTORAGE_KEYS.serviceType, "")
      navigate(GetNavigatePath([SceneConfig.SCENE_ACCOUNT, SceneConfig.PARTS_TOP]));
    }
    else {
      dispatch(setErrorModal({ show: true, text: response.error_detail }));
    }

    dispatch(setLoading(false));
  }

  if (! isMenu && initializeState > Constants.INITIALIZE_STATE.allComplete)
    return (
      <Stack className="menu-root">
      </Stack>
    );
  
  // 子要素のコンポーネント定義
  function MenuItem(props) {
    const location = useLocation();

    let isSelected = (GetNavigatePath(props.destination) === location.pathname);
    if (props.groupPage)
    {
      props.groupPage.forEach(element => {
        if (location.pathname == GetNavigatePath(element))
        {
          isSelected = true;
        }
      });
    }
    return (
      <div className={"menu-item " + (isSelected ? "selected" : "") + (props.disabled ? " disabled" : "")}>
        <Button
          className="hidden flex flex-both no-padding"
          onClick={() => props.onClick(props.destination)}
          disabled={props.disabled}
        >
          <div className="menu-item-icon-area padding-12px flex flex-center">
            <Icon icon={props.icon} isIconFont={props.isIconFont} size={props.isIconFont ? 20 : 28} />
          </div>
          <div className="padding-12px">{props.title}</div>
        </Button>
      </div>
    );
  }

  // 子要素のコンポーネント定義
  function MenuGroup(props) {
    const location = useLocation();
    let isSelected = false;
    if (props.groupPage)
    {
      props.groupPage.forEach(element => {
        if (location.pathname == GetNavigatePath(element))
        {
          isSelected = true;
        }
      });
    }
    const [isGroupOpen, setIsGroupOpen] = useState(isSelected || LocalStorageUtils.GetItem("openMenuGroup") == props.groupName);
  
    return (
      <div className={"menu-group " + (isGroupOpen ? " group-open" : "")}> 
        <Button className="hidden flex flex-both no-padding" onClick={() => {
            setIsGroupOpen(!isGroupOpen);
            LocalStorageUtils.SetItem("openMenuGroup", !isGroupOpen ? props.groupName : "");
        }}> 
          <div className="menu-item-icon-area padding-12px flex flex-center">
            <Icon className="group-icon" icon="caret-right-fill" isIconFont={true} size={18} />
          </div>
          <div className="padding-12px">{props.title}</div>
        </Button>
        <Stack className="menu-group-list">
          {props.children}
        </Stack>
      </div>
    );
  }
  
  return (
    <div className={"menu-drawer " + (isMenuOpen ? "open shadow-ease" : "")}>
          <div className="menu-header" onClick={onChangeMenu} title={isMenuOpen ? "メニューを折りたたみます" : "メニューを開きます"}>
            <div className="menu-header-row">
              {/* 閉じている時はロゴの先頭（マーク部分）だけを見せる */}
              <button type="button" className="menu-logo-button">
                <img src={logo} alt="PRizmo" className="logo"/>
              </button>
              <button type="button" className="menu-switch-button" tabIndex={isMenuOpen ? 0 : -1}>
                <Icon icon="chevron-left" size={16} />
              </button>
            </div>
          </div>
          {/* スクロールはメニュー一覧の中だけで行い、ヘッダーとログアウトは固定する */}
          <div className="menu-list">
            { isOpsHome ?
              // 運営ホームテナントの運営はテナント作成のための入口なので、使う画面だけを出す
              <>
                <MenuItem title="ユーザー管理"       isText={isMenuOpen} icon="people-fill"          isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_MANAGE]}></MenuItem>
                <MenuItem title="操作ログ"           isText={isMenuOpen} icon="clock-history"        isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_ACTION_LOG]}></MenuItem>
                <div className="menu-ops-section">
                  <div className="menu-ops-heading" title="運営権限のユーザーにのみ表示されます">
                    <div className="menu-ops-heading-label">運営関連</div>
                  </div>
                  <MenuItem title="テナント作成"       isText={isMenuOpen} icon="building-add"         isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_OPS_TENANT]}></MenuItem>
                </div>
              </>
            :
              <>
                <MenuItem title="基本情報"   isText={isMenuOpen} icon="info" isIconFont={false}               onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USEREDIT]}></MenuItem>
                <MenuItem title="表示設定"   isText={isMenuOpen} icon="display" isIconFont={false}              onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_PROFILE_EDIT]} disabled={isAppearanceEmpty}></MenuItem>
                {/* <MenuGroup title="RAG" icon="rag" groupName={"rag-group"} groupPage={[[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_STRUCTURE], [SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_UPLOAD], [SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_EVALUATE], [SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_PLAYGROUND]]}> */}
                  <MenuItem title="データ構造化"      isText={isMenuOpen} icon="grid-1x2"             isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_STRUCTURE]} ></MenuItem>
                  <MenuItem title="RAGファイル管理"   isText={isMenuOpen} icon="file-earmark-text"    isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_UPLOAD]} ></MenuItem>
                  <MenuItem title="RAG応答テスト"     isText={isMenuOpen} icon="check-all"            isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_EVALUATE]} ></MenuItem>
                  <MenuItem title="Playground"       isText={isMenuOpen} icon="chat-left-dots"       isIconFont={true}      onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_RAG_PLAYGROUND]} ></MenuItem>
                {/* </MenuGroup> */}
                <MenuItem title="ログ"               isText={isMenuOpen} icon="log"                   isIconFont={false}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_CHATLOG]}></MenuItem>
                { tUserAdd && tUserAdd.report_system_prompt != null && tUserAdd.report_system_prompt != "" &&
                  <MenuItem title="レポート"           isText={isMenuOpen} icon="file-earmark-text"     isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_CHATREPORT]}></MenuItem>
                }
                <MenuItem title="有人引き継ぎ設定"     isText={isMenuOpen} icon="person-fill-gear"     isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_INQUIRY_SETTING]}></MenuItem>
                <MenuItem title="有人引き継ぎ一覧"     isText={isMenuOpen} icon="person-lines-fill"    isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_INQUIRY_LOG]}></MenuItem>
                { canManageAccount(tAccount?.auth) &&
                  <>
                    <MenuItem title="ユーザー管理"       isText={isMenuOpen} icon="people-fill"          isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_MANAGE]}></MenuItem>
                    <MenuItem title="操作ログ"           isText={isMenuOpen} icon="clock-history"        isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_USER_ACTION_LOG]}></MenuItem>
                  </>
                }
                { canUseOperatorTool(tAccount?.auth) &&
                  <div className="menu-ops-section">
                    <div className="menu-ops-heading" title="運営権限のユーザーにのみ表示されます">
                      <div className="menu-ops-heading-label">運営関連</div>
                    </div>
                    <MenuItem title="プロンプト管理"     isText={isMenuOpen} icon="stars"                isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_OPS_PROMPT]}></MenuItem>
                    <MenuItem title="外見動画管理"       isText={isMenuOpen} icon="film"                 isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_OPS_APPEARANCE]}></MenuItem>
                    { Constants.IS_DEMO_PAGE_AVAILABLE &&
                      <MenuItem title="デモページ作成"     isText={isMenuOpen} icon="easel"                isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_OPS_DEMO_PAGE]}></MenuItem>
                    }
                    <MenuItem title="利用設定"           isText={isMenuOpen} icon="sliders"              isIconFont={true}     onClick={onClickSceneButton} destination={[SceneConfig.SCENE_ADMIN, SceneConfig.PARTS_ADMIN_OPS_CONFIG]}></MenuItem>
                  </div>
                }
              </>
            }
          </div>
          <div className="menu-footer">
            <button type="button" className="logout-item" title="ログアウト" onClick={onClickLogoutButton}>
              <div className="logout-item-icon-area">
                <Icon icon="logout" isIconFont={false} size={20} />
              </div>
              <div className="logout-label">ログアウト</div>
            </button>
          </div>
    </div>
  );
}

// 子要素のコンポーネント定義

function MenuItem(props) {
  const location = useLocation();

  let isSelected = (GetNavigatePath(props.destination) === location.pathname);
  if (props.groupPage)
  {
    props.groupPage.forEach(element => {
      if (location.pathname == GetNavigatePath(element))
      {
        isSelected = true;
      }
    });
  }
  return (
    <div className={"menu-item " + (isSelected ? "selected" : "") + (props.disabled ? " disabled" : "")}> 
      <Button
        className="hidden flex flex-both no-padding"
        onClick={() => props.onClick(props.destination)}
        disabled={props.disabled}
      >
        <div className="menu-item-icon-area padding-12px flex flex-center">
          <Icon icon={props.icon} isIconFont={false} size={28} />
        </div>
        <div className="padding-12px">{props.title}</div>
      </Button>
    </div>
  );
}
